'use client';
import React, { useEffect, useState } from 'react';
import { MemberProfile } from '@/lib/supabase/types';
import { PreviewProfile } from '@/lib/supabase/types';
import { X, Heart, ThumbsDown, Shield, Flag, MapPin, CheckCircle, Calendar, Target, BookOpen, Lock, AlertTriangle, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { jsnService } from '@/lib/supabase/jsnService';
import { createClient } from '@/lib/supabase/client';
import Link from 'next/link';
import { getPublicDisplayName } from '@/lib/supabase/nameUtils';

type ProfileModalProfile = MemberProfile | (PreviewProfile & { profileStatus?: string; profileComplete?: number });

interface ProfileModalProps {
  profile: ProfileModalProfile;
  isLiked: boolean;
  onLike: () => void;
  onClose: () => void;
  onBlock?: (id: string, displayName: string) => void;
  isPhotoBlocked?: boolean;
  photoStatus?: string;
  currentUserId?: string;
  isPreview?: boolean;
}

const statusConfig: Record<string, { label: string; className: string }> = {
  online: { label: 'Online Now', className: 'status-online' },
  new: { label: 'New Member', className: 'status-new' },
  verified: { label: 'Verified', className: 'status-verified' },
  premium: { label: 'Premium', className: 'status-premium' },
  offline: { label: 'Offline', className: 'bg-muted text-muted-foreground' },
};

export default function ProfileModal({ profile, isLiked, onLike, onClose, onBlock, isPhotoBlocked, photoStatus, currentUserId, isPreview }: ProfileModalProps) {
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [isPremium, setIsPremium] = useState(false);

  const publicName = getPublicDisplayName({
    firstName: (profile as MemberProfile).firstName,
    lastName: (profile as MemberProfile).lastName,
    displayName: profile.displayName,
  });

  useEffect(() => {
    const load = async () => {
      if (isPreview) return; // No need to check premium for preview profiles
      const supabase = createClient();
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const up = await jsnService.getUserProfileById(user.id);
        setIsPremium(up?.membershipTier === 'premium');
      }
    };
    load();
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [onClose, isPreview]);

  const profileStatus = (profile as MemberProfile).profileStatus;
  const status = profileStatus ? statusConfig[profileStatus] : null;

  const handleLike = () => {
    if (isPhotoBlocked) {
      const msg = photoStatus === 'pending' ?'Your profile photo is pending review. You can like or message members after approval.' :'Your profile photo was rejected. Please upload a replacement photo to like or message members.';
      toast.warning(msg, { duration: 4000 });
      return;
    }
    onLike();
    if (!isLiked) {
      if (isPreview) {
        toast.success(`You liked ${publicName}!`, {
          description: 'This is a preview profile. Keep browsing for active members.',
        });
      } else {
        toast.success(`You liked ${publicName}!`, { description: "If they like you back, it's a match." });
      }
    }
  };

  const handleMessage = () => {
    // Preview profiles: messaging always blocked
    if (isPreview) {
      toast.info('This profile is not available for messaging. Please continue browsing other profiles or check back soon.', {
        duration: 5000,
      });
      return;
    }
    if (isPhotoBlocked) {
      const msg = photoStatus === 'pending' ?'Your profile photo is pending review. You can like or message members after approval.' :'Your profile photo was rejected. Please upload a replacement photo to like or message members.';
      toast.warning(msg, { duration: 4000 });
      return;
    }
    if (isPremium) {
      window.location.href = '/messages';
    } else {
      toast.info('Premium required to send messages', {
        description: 'Upgrade to Premium to unlock messaging and more features.',
        action: { label: 'Upgrade Now', onClick: () => { window.location.href = '/membership'; } },
      });
    }
  };

  const handleBlock = () => {
    if (isPreview) return; // Cannot block preview profiles
    if (!isPremium) {
      toast.info('Upgrade to Premium to manage member interactions.', {
        description: 'Upgrade to Premium to unlock messaging, blocking, and more.',
        action: { label: 'Upgrade Now', onClick: () => { window.location.href = '/membership'; } },
        duration: 4000,
      });
      return;
    }
    if (onBlock) {
      onBlock(profile.id, publicName);
    } else if (currentUserId) {
      jsnService.blockUser(currentUserId, profile.id);
      toast.success('Profile blocked.', { description: 'You will no longer see this member in Discover or Matches.' });
      onClose();
    }
  };

  const handleReport = async () => {
    if (isPreview) return; // Cannot report preview profiles
    if (!isPremium) {
      toast.info('Upgrade to Premium to access member safety tools.', {
        description: 'Upgrade to Premium to unlock messaging, reporting, and more.',
        action: { label: 'Upgrade Now', onClick: () => { window.location.href = '/membership'; } },
        duration: 4000,
      });
      return;
    }
    if (!reportReason) { toast.error('Please select a reason for reporting'); return; }
    if (!currentUserId) return;
    await jsnService.reportUser(currentUserId, profile.id, reportReason, reportDetails);
    toast.success('Report submitted', { description: 'Our team will review this profile. Thank you for keeping JSN safe.' });
    setShowReportForm(false);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-card border border-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto scale-in">
        <div className="sticky top-0 z-10 bg-card/95 backdrop-blur-sm border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="font-bold text-foreground text-lg">{publicName}&apos;s Profile</h2>
            {isPreview && (
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold flex items-center bg-[#FFBF00]/20 border border-[#FFBF00]/40 text-[#FFBF00] dark:text-amber-400">
                <Eye size={10} className="mr-1" />Preview Profile
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-colors" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Preview profile notice */}
        {isPreview && (
          <div className="mx-6 mt-4 flex items-start gap-2.5 bg-[#FFBF00]/10 border border-[#FFBF00]/25 rounded-xl px-4 py-3">
            <AlertTriangle size={14} style={{ color: '#FFBF00' }} className="flex-shrink-0 mt-0.5" />
            <p className="text-xs font-medium" style={{ color: '#FFBF00' }}>
              Messaging is not available for this user
            </p>
          </div>
        )}

        {isPhotoBlocked && !isPreview && (
          <div className="mx-6 mt-4 flex items-start gap-2.5 bg-[#FFBF00]/10 border border-[#FFBF00]/25 rounded-xl px-4 py-3">
            <AlertTriangle size={14} style={{ color: '#FFBF00' }} className="flex-shrink-0 mt-0.5" />
            <p className="text-xs font-medium" style={{ color: '#FFBF00' }}>
              {photoStatus === 'pending' ?'Your photo is pending review. Liking and messaging will be available after approval.' :'Your photo was rejected. Upload a replacement to like or message members.'}
            </p>
          </div>
        )}

        <div className="relative h-48 flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${profile.avatarColor}20, ${profile.avatarColor}40)` }}>
          {profile.photoUrl ? (
            <img src={profile.photoUrl} alt={`Profile photo of ${profile.displayName}`} className="absolute inset-0 w-full h-full object-cover" />
          ) : (
            <div className="w-28 h-28 rounded-2xl flex items-center justify-center text-white font-black text-4xl shadow-lg"
              style={{ background: `linear-gradient(135deg, ${profile.avatarColor}, ${profile.avatarColor}99)` }}>
              {profile.avatarInitials}
            </div>
          )}
          {/* Show Preview badge in modal header image, not status */}
          {isPreview ? (
            <span className="absolute top-4 right-4 text-xs px-2.5 py-1 rounded-full font-semibold bg-[#FFBF00]/20 border border-[#FFBF00]/40 text-[#FFBF00] flex items-center gap-1">
              <Eye size={10} />Preview
            </span>
          ) : (
            status && (
              <span className={`absolute top-4 right-4 text-xs px-2.5 py-1 rounded-full font-semibold ${status.className}`}>{status.label}</span>
            )
          )}
        </div>

        <div className="p-6 space-y-6">
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="text-2xl font-black text-foreground">{publicName}, {profile.age}</h3>
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
                  <MapPin size={13} />{profile.usState}, USA
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-sm font-semibold text-foreground">{profile.kenyanCounty}</div>
                <div className="text-xs text-muted-foreground mt-0.5">Kenyan County</div>
              </div>
            </div>
            {!isPreview && (
              <div className="flex items-center gap-3">
                <div className="flex-1 h-1.5 bg-border rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${(profile as MemberProfile).profileComplete}%`, backgroundColor: '#008000' }} />
                </div>
                <span className="text-xs text-muted-foreground">{(profile as MemberProfile).profileComplete}% complete</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: Target, label: 'Intention', value: profile.intention },
              { icon: BookOpen, label: 'Faith', value: profile.faith },
              { icon: MapPin, label: 'Origin', value: `${profile.kenyanCounty}, Kenya` },
              { icon: Calendar, label: 'Gender', value: profile.gender },
            ].map((item) => {
              const ItemIcon = item.icon;
              return (
                <div key={`modal-detail-${item.label}`} className="bg-muted/20 border border-border rounded-xl p-3.5">
                  <div className="flex items-center gap-2 mb-1">
                    <ItemIcon size={13} className="text-primary" />
                    <span className="text-xs text-muted-foreground font-medium">{item.label}</span>
                  </div>
                  <div
                    className="text-sm font-semibold"
                    style={item.label === 'Intention' ? { color: '#008000' } : { color: 'var(--foreground)' }}
                  >
                    {item.value}
                  </div>
                </div>
              );
            })}
          </div>

          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">About</h4>
            <p className="text-sm text-foreground leading-relaxed">{profile.bio}</p>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Interests</h4>
            <div className="flex flex-wrap gap-2">
              {profile.interests.map((interest) => (
                <span key={`modal-int-${interest.slice(0, 15)}`} className="text-xs px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20 text-primary font-medium">
                  {interest}
                </span>
              ))}
            </div>
          </div>

          {!isPreview && (profile as MemberProfile).profileStatus === 'verified' && (
            <div className="flex items-center gap-2 p-3 bg-accent/10 border border-accent/20 rounded-xl">
              <CheckCircle size={16} className="text-accent flex-shrink-0" />
              <p className="text-xs text-foreground">This member has a verified profile on JSN.</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <button onClick={handleLike}
              className={`flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-all duration-200 ${
                isPhotoBlocked ? 'bg-muted/20 border border-dashed border-[#FFBF00]/40 text-[#FFBF00]/70 cursor-not-allowed' : ''
              }`}
              style={
                !isPhotoBlocked
                  ? isLiked
                    ? { backgroundColor: 'rgba(0,128,0,0.2)', border: '1px solid #008000', color: '#008000' }
                    : { background: 'linear-gradient(135deg, #8a0000 0%, #750000 100%)', color: '#ffffff' }
                  : {}
              }
            >
              <Heart size={16} style={isLiked && !isPhotoBlocked ? { fill: '#008000', color: '#008000' } : {}} />
              {isLiked && !isPhotoBlocked ? 'Liked ✓' : 'Like Profile'}
            </button>

            {/* Message button — always blocked for preview profiles */}
            <button onClick={handleMessage}
              className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold bg-muted/20 border border-border text-muted-foreground cursor-not-allowed transition-all"
              title={isPreview ? 'Messaging not available for preview profiles' : undefined}
            >
              <Lock size={16} />
              {isPreview ? 'Not Available' : (isPremium && !isPhotoBlocked ? 'Send Message' : 'Message (Premium)')}
            </button>

            <button onClick={() => { toast(`Passed on ${profile.displayName}`); onClose(); }}
              className="flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-medium bg-muted/20 border border-border text-muted-foreground hover:text-foreground transition-all">
              <ThumbsDown size={16} />Pass
            </button>

            {!isPreview && (
              <div className="flex gap-2">
                <button onClick={handleBlock}
                  className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl text-sm font-medium bg-muted/20 border border-border text-muted-foreground hover:text-foreground transition-all"
                  title="Block user">
                  <Shield size={15} />Block
                </button>
                <button onClick={() => {
                  if (!isPremium) {
                    toast.info('Upgrade to Premium to access member safety tools.', {
                      action: { label: 'Upgrade', onClick: () => { window.location.href = '/membership'; } },
                      duration: 4000,
                    });
                    return;
                  }
                  setShowReportForm(true);
                }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-3 rounded-xl text-sm font-medium bg-muted/20 border border-border text-muted-foreground hover:text-primary transition-all"
                  title="Report user">
                  <Flag size={15} />Report
                </button>
              </div>
            )}
          </div>

          {/* Preview profile messaging notice */}
          {isPreview && (
            <div className="border border-[#FFBF00]/30 rounded-xl p-4 bg-[#FFBF00]/5 text-center space-y-1">
              <Lock size={16} style={{ color: '#FFBF00' }} className="mx-auto" />
              <p className="text-sm font-semibold text-foreground">Messaging Not Available</p>
              <p className="text-xs text-muted-foreground">This profile is not available for messaging. Please continue browsing other profiles or check back soon.</p>
            </div>
          )}

          {showReportForm && !isPreview && (
            <div className="border border-primary/30 rounded-xl p-4 space-y-3 bg-primary/5">
              <h4 className="font-semibold text-foreground text-sm">Report {profile.displayName}</h4>
              <select className="input-jsn text-sm" value={reportReason} onChange={e => setReportReason(e.target.value)}>
                <option value="">Select reason</option>
                <option value="Inappropriate content">Inappropriate content</option>
                <option value="Fake profile">Fake profile / Impersonation</option>
                <option value="Harassment">Harassment or threatening behavior</option>
                <option value="Spam">Spam or scam</option>
                <option value="Underage">Appears to be underage</option>
                <option value="Other">Other</option>
              </select>
              <textarea
                className="input-jsn text-sm resize-none"
                rows={2}
                placeholder="Additional details (optional)"
                value={reportDetails}
                onChange={e => setReportDetails(e.target.value)}
              />
              <div className="flex gap-2">
                <button onClick={handleReport} className="btn-primary px-4 py-2 rounded-lg text-sm font-semibold flex-1">Submit Report</button>
                <button onClick={() => setShowReportForm(false)} className="btn-ghost px-4 py-2 rounded-lg text-sm">Cancel</button>
              </div>
            </div>
          )}

          {!isPremium && !isPhotoBlocked && !isPreview && (
            <div className="border border-primary/30 rounded-xl p-4 bg-[#1a1a1a] text-center space-y-2">
              <div className="text-xl">👑</div>
              <p className="text-sm font-bold text-white">Upgrade to Premium</p>
              <div className="flex items-center justify-center gap-2">
                <span className="text-xl font-black text-white">$29.99</span>
                <span className="text-[#a0a0a0] text-xs">per month</span>
              </div>
              <p className="text-xs text-[#a0a0a0]">Send messages, see who liked you, and unlock advanced features.</p>
              <Link href="/membership" className="btn-primary px-6 py-2 rounded-lg text-sm font-semibold inline-block mt-1">
                Upgrade Now
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}